import { useCallback, useMemo, useState } from 'react';

import { Modal, Table } from '@components/common';

import useConsistencyCheckHistoryInfoSummary from '@hooks/cmModule/consistencyCheckHistory/useConsistencyCheckHistoryInfoSummary';
import useConsistencyCheckHistorySummary from '@hooks/cmModule/consistencyCheckHistory/useConsistencyCheckHistorySummary';
import useConsistencyCheckHistorySummaryPathStatuses from '@hooks/cmModule/consistencyCheckHistory/useConsistencyCheckHistorySummaryPathStatuses';

import { IConsistencyCheckHistorySummary } from '@interfaces/cmModule/consistencyCheck';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { ISortBy, ITablePagination, ITableSearch, ITableSort, TTableFilter } from '@interfaces/components/table';

import { defaultMeta } from '@enums/common';

import { MIN_FILTER_WIDTH } from '@constants/common';
import { TableFilterTypes } from '@constants/table';

import ConsistencyCheckHistoryDetails from './ConsistencyCheckHistoryDetails';
import ConsistencyCheckHistoryExtraPathDetails from './ConsistencyCheckHistoryExtraPathDetails';
import { getColumns } from './utils';

import styles from './ConsistencyCheckHistorySummary.module.scss';

const MODAL_SIZE = '80%';

interface IConsistencyCheckHistorySummaryProps {
	dumpConsistencyId?: string;
}

const ConsistencyCheckHistorySummary = ({
	dumpConsistencyId,
}: IConsistencyCheckHistorySummaryProps) => {
	const [page, setPage] = useState(defaultMeta.page);
	const [pageSize, setPageSize] = useState(defaultMeta.pageSize);
	const [sortDirection, setSortDirection] = useState(false);
	const [sortFieldName, setSortFieldName] = useState<string | null>(null);
	const [searchValue, setSearchValue] = useState('');

	const [selectedPathStatus, setSelectedPathStatus] = useState<TSelectOption<string>>(null);

	const [isOpenDetailsModal, setIsOpenDetailsModal] = useState(false);
	const [isOpenExtraDetailsModal, setIsOpenExtraDetailsModal] = useState(false);
	const [selectedParameter, setSelectedParameter] = useState<IConsistencyCheckHistorySummary | null>(null);

	const closeExtraDetailsModal = useCallback(() => {
		setIsOpenExtraDetailsModal(false);
		setSelectedParameter(null);
	}, []);

	const openExtraDetailsModal = useCallback((parameter: IConsistencyCheckHistorySummary) => {
		setSelectedParameter(parameter);
		setIsOpenExtraDetailsModal(true);
	}, []);

	const closeConsistencySummary = useCallback(() => {
		setIsOpenDetailsModal(false);
		setSelectedParameter(null);
	}, []);

	const openDetailsModal = useCallback((parameter: IConsistencyCheckHistorySummary) => {
		setSelectedParameter(parameter);
		setIsOpenDetailsModal(true);
	}, []);

	const pageHandler = useCallback((page: number) => setPage(page), []);
	const pageSizeHandler = useCallback((pageSize: number) => {
		setPageSize(pageSize);
		setPage(defaultMeta.page);
	}, []);

	const {
		consistencyCheckHistorySummary,
		countOfRecords,
		isLoadingConsistencyCheckHistorySummary,
	} = useConsistencyCheckHistorySummary({
		dumpConsistencyId,
		page,
		pageSize,
		sortDirection,
		sortFieldName,
		searchValue,
		pathStatus: selectedPathStatus?.value || null,
	});

	const {
		consistencyCheckHistorySummaryPathStatuses,
		isLoadingConsistencyCheckHistorySummaryPathStatuses,
	} = useConsistencyCheckHistorySummaryPathStatuses();

	const {
		consistencyCheckInfoSummary,
		isLoadingConsistencyCheckHistoryInfoSummary,
	} = useConsistencyCheckHistoryInfoSummary({ id: dumpConsistencyId });

	const columns = useMemo(() => getColumns({
		viewParameter: openDetailsModal,
		viewExtraParameter: openExtraDetailsModal,
	}), [openDetailsModal, openExtraDetailsModal]);

	const pagination: ITablePagination = useMemo(() => ({
		countOfRecords: countOfRecords,
		forcePage: page,
		size: pageSize,
		pageHandler,
		pageSizeHandler,
	}), [
		page,
		pageSize,
		countOfRecords,
		pageHandler,
		pageSizeHandler,
	]);

	const handleSort = useCallback((sortBy: ISortBy[]) => {
		setSortDirection(!!sortBy[0]?.desc);
		setSortFieldName(sortBy[0]?.id);
		setPage(defaultMeta.page);
	}, []);

	const sort: ITableSort = useMemo(() => ({
		initialState: [{ id: sortFieldName, desc: sortDirection }],
		handleSort,
	}), [sortFieldName, sortDirection, handleSort]);

	const searchHandler = useCallback((searchValue: string) => {
		setSearchValue(searchValue);
		setPage(defaultMeta.page);
	}, []);

	const search: ITableSearch[] = useMemo(() => [{
		initialValue: searchValue,
		name: 'name',
		handler: searchHandler,
		tooltipText: 'Search by path',
	}], [searchValue, searchHandler]);

	const handlePathStatusChange = useCallback((option: TSelectOption<string>) => {
		setSelectedPathStatus(option);
		setPage(defaultMeta.page);
	}, []);

	const filters: TTableFilter[] = useMemo(() => {
		return [
			{
				name: 'pathStatus',
				placeholder: 'Path Status',
				option: selectedPathStatus,
				options: consistencyCheckHistorySummaryPathStatuses,
				minWidth: MIN_FILTER_WIDTH,
				type: TableFilterTypes.SELECT,
				setState: handlePathStatusChange,
				defaultValue: null,
			},
		];
	}, [
		consistencyCheckHistorySummaryPathStatuses,
		selectedPathStatus,
		handlePathStatusChange,
	]);

	const isLoading = isLoadingConsistencyCheckHistoryInfoSummary
		|| isLoadingConsistencyCheckHistorySummary
		|| isLoadingConsistencyCheckHistorySummaryPathStatuses;

	return (
		<div className={styles.wrapper}>
			<div className={styles.header}>
				<div className={styles.detail}>
					<div className={styles.name}>Network element:</div>
					<div className={styles.value}>{consistencyCheckInfoSummary?.networkElementName}</div>
				</div>
				<div className={styles.detail}>
					<div className={styles.name}>Reference template:</div>
					<div className={styles.value}>{consistencyCheckInfoSummary?.goldenTemplateName}</div>
				</div>
				<div className={styles.detail}>
					<div className={styles.name}>Consistency check time:</div>
					<div className={styles.value}>{consistencyCheckInfoSummary?.createdAtText}</div>
				</div>
			</div>
			<Modal
				height={MODAL_SIZE}
				isOpen={isOpenExtraDetailsModal}
				onClose={closeExtraDetailsModal}
				title={selectedParameter?.dataModelPath}
				width={MODAL_SIZE}
			>
				<ConsistencyCheckHistoryExtraPathDetails
					dataModelPathId={selectedParameter?.dataModelPathId}
					dumpConsistencyId={dumpConsistencyId}
				/>
			</Modal>
			<Modal
				height={MODAL_SIZE}
				isOpen={isOpenDetailsModal}
				onClose={closeConsistencySummary}
				title={selectedParameter?.dataModelPath}
				width={MODAL_SIZE}
			>
				<ConsistencyCheckHistoryDetails
					dataModelPathId={selectedParameter?.dataModelPathId}
					dumpConsistencyId={dumpConsistencyId}
				/>
			</Modal>
			<div className={styles.container}>
				<Table
					columns={columns}
					data={consistencyCheckHistorySummary}
					filters={filters}
					isDeleteActionDisabled
					isLoading={isLoading}
					isWithoutCheckbox
					isWithoutDeleteAction
					pagination={pagination}
					search={search}
					sort={sort}
				/>
			</div>
		</div>
	);
};

export default ConsistencyCheckHistorySummary;
